import { resupplyConfigFor } from '@1delta/data-sdk'
import {
  ConvexPoolUtilAbi,
  ResupplyPairAbi,
  ResupplyPairEmissionsAbi,
  ResupplyRegistryAbi,
  ResupplyRewardHandlerAbi,
  ResupplyUtilitiesAbi,
} from '@1delta/abis'
import { erc20Abi } from 'viem'
import { multicallRetryUniversal } from '@1delta/providers'
import { setResupplyPairRoster } from '../../../prices/oracle-prices/fetchers/resupplyRoster'
import type {
  ResupplyMarketsRaw,
  ResupplyPairIdentity,
  ResupplyPairRaw,
  ResupplyRsupEmissions,
} from './types'

/**
 * Minimal ERC-4626 fragment — only the one getter Resupply's own oracle uses.
 * Declared here rather than pulled from a shared 4626 ABI so the per-call ABI
 * array stays exact (the decode-by-name trap).
 */
const VAULT_PRICE_ABI = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'shares', type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

/**
 * The wrapped market's own collateral getter — the asset the EXTERNAL market
 * lends against (sfrxUSD, WBTC, …), which is what visually distinguishes one
 * Resupply pair from another.
 *
 * The two families spell it differently, and that difference is also how
 * Resupply's own `Utilities` tells them apart: a Curve Lend vault answers
 * `collateral_token()`, a Fraxlend pair answers `collateralContract()`. Both
 * are tried; whichever answers identifies the family.
 */
const WRAPPED_COLLATERAL_ABI = [
  {
    name: 'collateral_token',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
  {
    name: 'collateralContract',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
] as const

/** Identity reads per pair — immutable, so they run once and are cached. */
const IDENTITY_READS = 3
/** State reads per pair — keep in lock-step with the slicer below. */
const STATE_READS = 14

const ONE = 10n ** 18n

/**
 * Pair identity is immutable, so cache it forever, keyed by chain + pair. The
 * roster only ever grows within a process (governance adds pairs; retiring one
 * zeroes `borrowLimit` rather than removing it), so this bounds cold reads to
 * the pairs we have not seen yet.
 */
const identityCache = new Map<string, ResupplyPairIdentity>()
const identityKey = (chainId: string, pair: string) =>
  `${chainId}:${pair.toLowerCase()}`

const big = (v: any): bigint | null => {
  if (typeof v === 'bigint') return v
  if (typeof v === 'number') return BigInt(v)
  return null
}

/**
 * Fetch every Resupply market on a chain — FULLY ON-CHAIN, no API and no
 * published market roster.
 *
 * Discovery is `ResupplyRegistry.getAllPairAddresses()`. That is deliberate:
 * governance adds pairs (7 appeared between the docs' published list and
 * 2026-08) and retires them by zeroing `borrowLimit`, so a static file would
 * both miss new markets and advertise frozen ones as borrowable. The registry
 * is permissionless to read and is the same source the protocol's own
 * periphery uses.
 *
 * Three rounds cold, two warm:
 *   1. registry → pair addresses (intersected with `pairAllowlist` if set);
 *   2. identity (`name`/`collateral`/`underlying` + both tokens' decimals) for
 *      pairs not already cached — immutable, so this is once per pair ever;
 *   3. state: terms, accounting, rates and the collateral vault's share price.
 *
 * The rate is read from the `Utilities` lens rather than `currentRateInfo`,
 * which is only a checkpoint from the last write — on a quiet pair that can be
 * hours stale, and the off-peg amplifier moves with the reUSD price. The
 * checkpoint is kept as a fallback.
 */
export async function fetchResupplyMarkets(
  lender: string,
  chainId: string,
): Promise<ResupplyMarketsRaw> {
  const config = resupplyConfigFor(lender, chainId)
  if (!config?.registry) return { lender, config, pairs: [] }

  // --- Round 1: discovery ---
  let discovered: string[] = []
  try {
    const [addresses] = (await multicallRetryUniversal({
      chain: chainId,
      calls: [
        { address: config.registry, name: 'getAllPairAddresses', params: [] },
      ],
      abi: ResupplyRegistryAbi as any,
      allowFailure: true,
    })) as any[]
    if (Array.isArray(addresses)) {
      discovered = addresses.filter(
        (a: any) => typeof a === 'string' && a.startsWith('0x'),
      )
    }
  } catch {
    return { lender, config, pairs: [] }
  }
  if (discovered.length === 0) return { lender, config, pairs: [] }

  if (config.pairAllowlist && config.pairAllowlist.length > 0) {
    const allow = new Set(config.pairAllowlist.map((p) => p.toLowerCase()))
    discovered = discovered.filter((p) => allow.has(p.toLowerCase()))
    if (discovered.length === 0) return { lender, config, pairs: [] }
  }

  // --- Round 2: identity for pairs we have never seen ---
  const unknown = discovered.filter(
    (p) => !identityCache.has(identityKey(chainId, p)),
  )
  if (unknown.length > 0) {
    const calls = unknown.flatMap((pair) => [
      { address: pair, name: 'name', params: [] },
      { address: pair, name: 'collateral', params: [] },
      { address: pair, name: 'underlying', params: [] },
    ])
    let res: any[] = []
    try {
      res = (await multicallRetryUniversal({
        chain: chainId,
        calls,
        abi: unknown.flatMap(() => [
          ResupplyPairAbi,
          ResupplyPairAbi,
          ResupplyPairAbi,
        ]) as any,
        allowFailure: true,
      })) as any[]
    } catch {
      return { lender, config, pairs: [] }
    }

    // A second hop, since we only just learned the token addresses. Also
    // immutable, so it rides along into the same cache entry.
    const pending: {
      pair: string
      name: string
      collateral: string
      underlying: string
    }[] = []
    unknown.forEach((pair, i) => {
      const base = i * IDENTITY_READS
      const name = res[base]
      const collateral = res[base + 1]
      const underlying = res[base + 2]
      if (
        typeof collateral !== 'string' ||
        !collateral.startsWith('0x') ||
        typeof underlying !== 'string' ||
        !underlying.startsWith('0x')
      ) {
        // Not a live pair (or the read failed) — skip rather than cache a
        // half-built identity we would then trust forever.
        return
      }
      pending.push({
        pair,
        name: typeof name === 'string' ? name : pair,
        collateral,
        underlying,
      })
    })

    if (pending.length > 0) {
      // Decimals of both tokens PLUS the wrapped market's own collateral —
      // the latter identifies the family and is the per-market image source.
      let dec: any[] = []
      try {
        dec = (await multicallRetryUniversal({
          chain: chainId,
          calls: pending.flatMap((p) => [
            { address: p.collateral, name: 'decimals', params: [] },
            { address: p.underlying, name: 'decimals', params: [] },
            { address: p.collateral, name: 'collateral_token', params: [] },
            { address: p.collateral, name: 'collateralContract', params: [] },
          ]),
          abi: pending.flatMap(() => [
            erc20Abi,
            erc20Abi,
            WRAPPED_COLLATERAL_ABI,
            WRAPPED_COLLATERAL_ABI,
          ]) as any,
          allowFailure: true,
        })) as any[]
      } catch {
        dec = []
      }

      // A FAILED multicall entry decodes to the literal `'0x'`, which is
      // truthy and starts with `0x` — so the test has to be the full 20-byte
      // shape or a Fraxlend pair (whose `collateral_token()` reverts) reads as
      // a Curve Lend vault with an empty address.
      const addr = (v: any): string | undefined =>
        typeof v === 'string' &&
        /^0x[0-9a-fA-F]{40}$/.test(v) &&
        !/^0x0+$/.test(v)
          ? v
          : undefined

      // Whichever getter answered identifies the family, exactly as Resupply's
      // own `Utilities.getUnderlyingSupplyRate` does it.
      const wrapped = pending.map((_, i) => {
        const curve = addr(dec[i * 4 + 2])
        const frax = addr(dec[i * 4 + 3])
        return {
          token: curve ?? frax,
          family: (curve ? 'curvelend' : frax ? 'fraxlend' : undefined) as
            | 'curvelend'
            | 'fraxlend'
            | undefined,
        }
      })
      pending.forEach((p, i) => {
        const cd = Number(dec[i * 4])
        const ud = Number(dec[i * 4 + 1])
        const w = wrapped[i]
        identityCache.set(identityKey(chainId, p.pair), {
          pair: p.pair,
          name: p.name,
          collateral: p.collateral,
          underlying: p.underlying,
          collateralDecimals: Number.isFinite(cd) && cd > 0 ? cd : 18,
          underlyingDecimals: Number.isFinite(ud) && ud > 0 ? ud : 18,
          wrappedCollateralToken: w?.token,
          wrappedFamily: w?.family,
        })
      })
    }
  }

  const identities = discovered
    .map((p) => identityCache.get(identityKey(chainId, p)))
    .filter((x): x is ResupplyPairIdentity => !!x)
  if (identities.length === 0) return { lender, config, pairs: [] }

  // --- Round 2b: chain-level reward plumbing ---
  // Resolved live rather than configured: `rewardHandler` is a registry entry
  // governance can repoint, and the whole stream can end (`periodFinish`).
  const { rsup, pairEmissions, convexPoolUtil } = await readRewardContext(
    chainId,
    config.registry,
    config.utilities,
  )

  // --- Round 3: live state ---
  const utilities = config.utilities
  const calls: any[] = []
  const abis: any[] = []
  for (const id of identities) {
    calls.push(
      { address: id.pair, name: 'maxLTV', params: [] },
      { address: id.pair, name: 'borrowLimit', params: [] },
      { address: id.pair, name: 'liquidationFee', params: [] },
      { address: id.pair, name: 'mintFee', params: [] },
      { address: id.pair, name: 'minimumBorrowAmount', params: [] },
      { address: id.pair, name: 'getPairAccounting', params: [] },
      { address: id.pair, name: 'currentRateInfo', params: [] },
      { address: id.pair, name: 'exchangeRateInfo', params: [] },
      // The collateral vault's own share price — the exact input Resupply's
      // `BasicVaultOracle` uses. Read LIVE rather than inverting the pair's
      // cached `exchangeRate`, which only moves when someone writes.
      { address: id.collateral, name: 'convertToAssets', params: [ONE] },
      // Rewards. `convexPid == 0` means the collateral is not staked, so there
      // is nothing to report; the weight is this pair's slice of the RSUP
      // stream. Both are governance-mutable, so neither is cached.
      { address: id.pair, name: 'convexPid', params: [] },
      {
        address: pairEmissions ?? id.pair,
        name: pairEmissions ? 'balanceOf' : 'convexPid',
        params: pairEmissions ? [id.pair] : [],
      },
    )
    abis.push(
      ResupplyPairAbi,
      ResupplyPairAbi,
      ResupplyPairAbi,
      ResupplyPairAbi,
      ResupplyPairAbi,
      ResupplyPairAbi,
      ResupplyPairAbi,
      ResupplyPairAbi,
      VAULT_PRICE_ABI,
      ResupplyPairAbi,
      pairEmissions ? ResupplyPairEmissionsAbi : ResupplyPairAbi,
    )
    // Lens reads. When no Utilities address is configured we still push
    // placeholders so the slicer's stride stays constant.
    if (utilities) {
      calls.push(
        { address: utilities, name: 'getPairInterestRate', params: [id.pair] },
        {
          address: utilities,
          name: 'getUnderlyingSupplyRate',
          params: [id.pair],
        },
        { address: id.pair, name: 'totalDebtAvailable', params: [] },
      )
      abis.push(ResupplyUtilitiesAbi, ResupplyUtilitiesAbi, ResupplyPairAbi)
    } else {
      calls.push(
        { address: id.pair, name: 'totalDebtAvailable', params: [] },
        { address: id.pair, name: 'totalDebtAvailable', params: [] },
        { address: id.pair, name: 'totalDebtAvailable', params: [] },
      )
      abis.push(ResupplyPairAbi, ResupplyPairAbi, ResupplyPairAbi)
    }
  }

  // Issue the state reads in PER-PAIR-GROUP batches rather than one ~250-call
  // request. This is not a micro-optimisation — it changes the failure mode.
  // Under `allowFailure: true` the multicall layer only retries when EVERY
  // call in a request fails; a partial transport failure (rate limiting, which
  // public RPCs do return mid-batch) is silently mapped to `'0x'` and would
  // surface here as a live pair with a null `maxLTV`. Splitting into chunks
  // makes such a failure an all-fail WITHIN its chunk, which the layer then
  // retries and fails over to the next RPC. Observed for real on a 252-call
  // batch during integration.
  const PAIRS_PER_BATCH = 6
  const results: any[] = []
  for (let i = 0; i < identities.length; i += PAIRS_PER_BATCH) {
    const from = i * STATE_READS
    const to = Math.min(i + PAIRS_PER_BATCH, identities.length) * STATE_READS
    try {
      const chunk = (await multicallRetryUniversal({
        chain: chainId,
        calls: calls.slice(from, to),
        abi: abis.slice(from, to) as any,
        allowFailure: true,
      })) as any[]
      results.push(...chunk)
    } catch {
      // This chunk's pairs are dropped by the converter's failed-read guard
      // rather than being published with zeroed terms.
      results.push(...new Array(to - from).fill('0x'))
    }
  }

  const pairs: ResupplyPairRaw[] = identities.map((identity, i) => {
    const base = i * STATE_READS
    const accounting = results[base + 5]
    const rateInfo = results[base + 6]
    const exchangeInfo = results[base + 7]

    const tuple = (v: any, idx: number): bigint | null =>
      Array.isArray(v) ? big(v[idx]) : null

    return {
      identity,
      maxLTV: big(results[base]),
      borrowLimit: big(results[base + 1]),
      liquidationFee: big(results[base + 2]),
      mintFee: big(results[base + 3]),
      minimumBorrowAmount: big(results[base + 4]),
      totalBorrowAmount: tuple(accounting, 1),
      totalBorrowShares: tuple(accounting, 2),
      totalCollateral: tuple(accounting, 3),
      ratePerSec: tuple(rateInfo, 1),
      exchangeRate: tuple(exchangeInfo, 2),
      collateralPrice: big(results[base + 8]),
      // Indices follow the PUSH ORDER above exactly: the two reward reads sit
      // at +9/+10, BEFORE the lens block, so the lens reads are +11/+12.
      // (Getting this wrong is silent — it produced a 3-billion-percent APR
      // before the numbers were checked against a hand computation.)
      convexPid: big(results[base + 9]),
      rsupWeight: pairEmissions ? big(results[base + 10]) : null,
      liveRatePerSec: utilities ? big(results[base + 11]) : null,
      underlyingSupplyRatePerSec: utilities ? big(results[base + 12]) : null,
      collateralRewards: [],
    }
  })

  // --- Round 4: Convex reward rates, only for pairs that actually stake ---
  await attachCollateralRewards(chainId, convexPoolUtil, pairs)

  // Publish the discovered pair -> underlying map for the oracle-price
  // fetcher, whose `getCalls` is synchronous and so cannot run the registry
  // discovery itself. See `resupplyRoster.ts` for why re-discovering there
  // would be wrong rather than merely wasteful.
  setResupplyPairRoster(
    chainId,
    lender,
    pairs.map((p) => ({
      pair: p.identity.pair.toLowerCase(),
      underlying: p.identity.underlying.toLowerCase(),
    })),
  )

  return { lender, config, pairs, rsup }
}

/** Test seam — drops the immutable-identity cache. */
export function __resetResupplyIdentityCache(): void {
  identityCache.clear()
}

/**
 * Resolve the chain-level reward plumbing: the RSUP emission stream and
 * Convex's rate lens.
 *
 * Both are read live rather than configured — `rewardHandler` is a registry
 * entry governance can repoint, and the stream itself expires
 * (`periodFinish`). Returns `rsup: undefined` when the stream has ENDED, which
 * is the difference between "borrowers earn nothing" and "we failed to read",
 * and the converter must not publish a stale rate for the former.
 */
async function readRewardContext(
  chainId: string,
  registry: string,
  utilities?: string | null,
): Promise<{
  rsup?: ResupplyRsupEmissions
  pairEmissions?: string
  convexPoolUtil?: string
}> {
  const addr = (v: any): string | undefined =>
    typeof v === 'string' && v.startsWith('0x') && !/^0x0+$/.test(v)
      ? v
      : undefined
  try {
    const first = (await multicallRetryUniversal({
      chain: chainId,
      calls: [
        { address: registry, name: 'rewardHandler', params: [] },
        { address: registry, name: 'govToken', params: [] },
        ...(utilities
          ? [{ address: utilities, name: 'convexPoolUtil', params: [] }]
          : []),
      ],
      abi: [
        ResupplyRegistryAbi,
        ResupplyRegistryAbi,
        ...(utilities ? [ResupplyUtilitiesAbi] : []),
      ] as any,
      allowFailure: true,
    })) as any[]

    const rewardHandler = addr(first[0])
    const govToken = addr(first[1])
    const convexPoolUtil = utilities ? addr(first[2]) : undefined
    if (!rewardHandler || !govToken) return { convexPoolUtil }

    const [emissions] = (await multicallRetryUniversal({
      chain: chainId,
      calls: [{ address: rewardHandler, name: 'pairEmissions', params: [] }],
      abi: ResupplyRewardHandlerAbi as any,
      allowFailure: true,
    })) as any[]
    const pairEmissions = addr(emissions)
    if (!pairEmissions) return { convexPoolUtil }

    const stream = (await multicallRetryUniversal({
      chain: chainId,
      calls: [
        { address: pairEmissions, name: 'periodFinish', params: [] },
        { address: pairEmissions, name: 'rewardRate', params: [] },
        { address: pairEmissions, name: 'totalSupply', params: [] },
      ],
      abi: [
        ResupplyPairEmissionsAbi,
        ResupplyPairEmissionsAbi,
        ResupplyPairEmissionsAbi,
      ] as any,
      allowFailure: true,
    })) as any[]

    const periodFinish = big(stream[0])
    const rewardRate = big(stream[1])
    const totalWeight = big(stream[2])
    if (
      periodFinish === null ||
      rewardRate === null ||
      totalWeight === null ||
      totalWeight === 0n
    ) {
      return { pairEmissions, convexPoolUtil }
    }
    // An expired stream pays nothing — report no `rsup` rather than a rate
    // that has already stopped accruing.
    if (periodFinish <= BigInt(Math.floor(Date.now() / 1000))) {
      return { pairEmissions, convexPoolUtil }
    }
    return {
      rsup: { govToken, rewardRate, totalWeight, periodFinish },
      pairEmissions,
      convexPoolUtil,
    }
  } catch {
    return {}
  }
}

/**
 * Attach Convex reward rates to the pairs that stake their collateral.
 *
 * `rewardRates(pid)` gives reward wei per second per 1e18 of STAKED SHARES
 * (verified against the pool's own `crvRewards.rewardRate()/totalSupply()`).
 * The token list can repeat an address — a pool may carry two streams of the
 * same token, one of them zero — so rates are AGGREGATED by token rather than
 * assumed unique.
 *
 * Failure is non-fatal: a market without reward data is still a valid market,
 * so the rows publish without a rewards block rather than not at all.
 */
async function attachCollateralRewards(
  chainId: string,
  convexPoolUtil: string | undefined,
  pairs: ResupplyPairRaw[],
): Promise<void> {
  if (!convexPoolUtil) return
  const staked = pairs.filter((p) => (p.convexPid ?? 0n) > 0n)
  if (staked.length === 0) return

  let res: any[] = []
  try {
    res = (await multicallRetryUniversal({
      chain: chainId,
      calls: staked.map((p) => ({
        address: convexPoolUtil,
        name: 'rewardRates',
        params: [p.convexPid],
      })),
      abi: staked.map(() => ConvexPoolUtilAbi) as any,
      allowFailure: true,
    })) as any[]
  } catch {
    return
  }

  staked.forEach((p, i) => {
    const entry = res[i]
    if (!Array.isArray(entry)) return
    const [tokens, rates] = entry as [string[], bigint[]]
    if (!Array.isArray(tokens) || !Array.isArray(rates)) return
    const byToken = new Map<string, bigint>()
    tokens.forEach((t, j) => {
      const rate = rates[j]
      if (typeof t !== 'string' || typeof rate !== 'bigint' || rate === 0n) {
        return
      }
      const key = t.toLowerCase()
      byToken.set(key, (byToken.get(key) ?? 0n) + rate)
    })
    p.collateralRewards = [...byToken.entries()].map(
      ([token, ratePerSecPerShare]) => ({ token, ratePerSecPerShare }),
    )
  })
}
